import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TablePagination,
  Chip,
  Avatar,
  InputAdornment,
  Skeleton,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip as MuiTooltip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  MdEdit,
  MdDelete,
  MdAdd,
  MdSearch,
  MdPersonOutline,
  MdPeople,
  MdAdminPanelSettings,
  MdBadge,
  MdGridView,
  MdViewList,
  MdPhone,
  MdEmail,
  MdRefresh,
  MdClear,
  MdWork,
} from "react-icons/md";
import toast from "react-hot-toast";
import { axiosInstance } from "../apis/config";
import { brand } from "../theme";

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

const roleChipStyle = (role) => {
  const key = (role || "").toLowerCase();
  if (key === "admin") return { backgroundColor: brand.ink950, color: brand.parchment };
  if (key === "vendor") return { backgroundColor: "#8B5CF622", color: "#6D28D9", border: "1px solid #8B5CF644" };
  return { backgroundColor: brand.brassSoft, color: brand.ink950 };
};

const avatarBg = (role) => {
  const key = (role || "").toLowerCase();
  if (key === "admin") return brand.ink950;
  if (key === "vendor") return "#6D28D9";
  return brand.brass;
};

const StatCard = ({ icon, label, count, accent }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: "20px",
      display: "flex",
      alignItems: "center",
      gap: 2,
      bgcolor: brand.paper,
      border: `1px solid ${brand.slateLight}40`,
      boxShadow: "0 10px 30px -15px rgba(11,10,61,0.06)",
      transition: "transform 200ms ease, box-shadow 200ms ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 18px 40px -18px rgba(11,10,61,0.12)",
        borderColor: `${brand.brass}66`,
      },
    }}
  >
    <Box
      sx={{
        width: 46,
        height: 46,
        borderRadius: "14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: `${accent || brand.brass}18`,
        color: accent || brand.ink950,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box minWidth={0}>
      <Typography
        sx={{
          fontFamily: '"Fraunces", serif',
          fontWeight: 700,
          fontSize: "1.8rem",
          color: brand.ink950,
          lineHeight: 1.1,
        }}
      >
        {count}
      </Typography>
      <Typography sx={{ color: brand.slate, fontSize: "0.82rem", fontWeight: 600 }} noWrap>
        {label}
      </Typography>
    </Box>
  </Paper>
);

const Employee = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [viewMode, setViewMode] = useState("table"); // 'table' | 'grid'
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openCreate, setOpenCreate] = useState(false);

  // Form State
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    role: "employee",
  });

  // Edit State
  const [openEdit, setOpenEdit] = useState(false);
  const [editForm, setEditForm] = useState({
    id: null,
    name: "",
    email: "",
    mobile: "",
    role: "employee",
  });

  // Delete State
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteEmp, setDeleteEmp] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const openDeleteDialog = (emp) => {
    setDeleteEmp(emp);
    setOpenDelete(true);
  };

  const handleDelete = async () => {
    if (!deleteEmp) return;
    setDeleting(true);
    try {
      const empId = deleteEmp.id || deleteEmp._id;
      const res = await axiosInstance.delete(`/employees/${empId}`);
      if (res.data.message || res.status === 200) {
        toast.success("Employee deleted successfully");
        setOpenDelete(false);
        setDeleteEmp(null);
        fetchEmployees();
      } else {
        toast.error("Failed to delete employee");
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || "Error deleting employee");
    } finally {
      setDeleting(false);
    }
  };

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/employees");
      setEmployees(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error("Failed to load employees list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const counts = useMemo(() => {
    let admin = 0;
    let employee = 0;
    let vendor = 0;
    let withMobile = 0;

    employees.forEach((emp) => {
      const r = (emp.role || "").toLowerCase();
      if (r === "admin") admin += 1;
      else if (r === "vendor") vendor += 1;
      else employee += 1;

      if (emp.mobile && emp.mobile.trim() !== "") withMobile += 1;
    });

    return { total: employees.length, admin, employee, vendor, withMobile };
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    return employees.filter((emp) => {
      const r = (emp.role || "employee").toLowerCase();
      if (roleFilter !== "all" && r !== roleFilter) return false;

      if (!search.trim()) return true;
      const q = search.toLowerCase();
      const name = (emp.name || "").toLowerCase();
      const email = (emp.email || "").toLowerCase();
      const mobile = (emp.mobile || "").toLowerCase();

      return name.includes(q) || email.includes(q) || mobile.includes(q);
    });
  }, [employees, roleFilter, search]);

  const paginatedEmployees = useMemo(() => {
    return filteredEmployees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredEmployees, page, rowsPerPage]);

  // Handlers
  const handleCreateChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const res = await axiosInstance.post("/admin/employees", form);
      if (res.data.success || res.status === 200 || res.status === 201) {
        toast.success("Employee created successfully!");
        setForm({ name: "", email: "", mobile: "", role: "employee" });
        setOpenCreate(false);
        fetchEmployees();
      } else {
        toast.error("Failed to create employee");
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || "Error creating employee");
    } finally {
      setCreating(false);
    }
  };

  const openEditDialog = (emp) => {
    setEditForm({
      id: emp.id || emp._id,
      name: emp.name || "",
      email: emp.email || "",
      mobile: emp.mobile || "",
      role: emp.role || "employee",
    });
    setOpenEdit(true);
  };

  const handleEditChange = (e) => {
    setEditForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      const res = await axiosInstance.post(`/employees/${editForm.id}`, editForm);
      if (res.data.message || res.status === 200) {
        toast.success("Employee updated successfully");
        setOpenEdit(false);
        fetchEmployees();
      } else {
        toast.error("Update failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || "Error updating employee");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Box p={{ xs: 2, sm: 3, md: 4 }} sx={{ maxWidth: 1600, margin: "0 auto" }}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography
              variant="h3"
              sx={{
                color: brand.ink950,
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
                fontSize: { xs: "1.8rem", md: "2.2rem" },
              }}
            >
              Employee Operations
            </Typography>
            <Chip
              label={`${employees.length} Staff`}
              size="small"
              sx={{ bgcolor: brand.brassSoft, color: brand.ink950, fontWeight: 700 }}
            />
          </Box>
          <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
            Manage organizational staff members, roles, and communication profiles
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <MuiTooltip title="Reload staff records">
            <IconButton
              onClick={fetchEmployees}
              sx={{
                bgcolor: brand.paper,
                border: `1px solid ${brand.slateLight}60`,
                p: 1.1,
                borderRadius: "14px",
                color: brand.ink950,
                "&:hover": { bgcolor: brand.brassSoft },
              }}
            >
              <MdRefresh size={20} className={loading ? "animate-spin" : ""} />
            </IconButton>
          </MuiTooltip>

          <Button
            variant="contained"
            color="secondary"
            onClick={() => setOpenCreate(true)}
            startIcon={<MdAdd size={20} />}
            sx={{
              px: 2.5,
              py: 1,
              borderRadius: "14px",
              fontWeight: 700,
              fontSize: "0.88rem",
              boxShadow: "0 8px 20px -6px rgba(201,162,39,0.5)",
            }}
          >
            Create Employee
          </Button>
        </Box>
      </Box>

      {/* ── Stat Cards Grid ─────────────────────────────────────────────── */}
      <Grid container spacing={2.5} mb={3.5}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<MdPeople size={24} />} label="Total Staff Members" count={counts.total} accent="#3B82F6" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MdAdminPanelSettings size={24} />}
            label="Administrative Staff"
            count={counts.admin}
            accent={brand.ink950}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MdBadge size={24} />}
            label="Field & Operations"
            count={counts.employee}
            accent={brand.brass}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MdWork size={24} />}
            label="Vendors & Partners"
            count={counts.vendor}
            accent="#8B5CF6"
          />
        </Grid>
      </Grid>

      {/* ── Control Bar ─────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "20px",
          bgcolor: brand.paper,
          border: `1px solid ${brand.slateLight}40`,
          mb: 3,
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          gap={2}
        >
          {/* Search Bar */}
          <TextField
            placeholder="Search employee by name, email, mobile..."
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{
              width: { xs: "100%", md: 320 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
                bgcolor: `${brand.slateLight}15`,
                fontSize: "0.85rem",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch color={brand.slate} size={20} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <MdClear size={16} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* Role Tabs & View Switcher */}
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip
                label={`All (${employees.length})`}
                onClick={() => {
                  setRoleFilter("all");
                  setPage(0);
                }}
                sx={{
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  px: 0.5,
                  bgcolor: roleFilter === "all" ? brand.ink950 : `${brand.slateLight}25`,
                  color: roleFilter === "all" ? brand.parchment : brand.slate,
                  "&:hover": { bgcolor: roleFilter === "all" ? brand.ink800 : `${brand.slateLight}40` },
                }}
              />
              <Chip
                label={`Employee (${counts.employee})`}
                onClick={() => {
                  setRoleFilter("employee");
                  setPage(0);
                }}
                sx={{
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  px: 0.5,
                  bgcolor: roleFilter === "employee" ? brand.brass : `${brand.slateLight}25`,
                  color: roleFilter === "employee" ? brand.ink950 : brand.slate,
                  "&:hover": { bgcolor: roleFilter === "employee" ? brand.brassLight : `${brand.slateLight}40` },
                }}
              />
              <Chip
                label={`Vendor (${counts.vendor})`}
                onClick={() => {
                  setRoleFilter("vendor");
                  setPage(0);
                }}
                sx={{
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  px: 0.5,
                  bgcolor: roleFilter === "vendor" ? "#8B5CF6" : `${brand.slateLight}25`,
                  color: roleFilter === "vendor" ? "#FFFFFF" : brand.slate,
                  "&:hover": { bgcolor: roleFilter === "vendor" ? "#7C3AED" : `${brand.slateLight}40` },
                }}
              />
              <Chip
                label={`Admin (${counts.admin})`}
                onClick={() => {
                  setRoleFilter("admin");
                  setPage(0);
                }}
                sx={{
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  px: 0.5,
                  bgcolor: roleFilter === "admin" ? brand.ink950 : `${brand.slateLight}25`,
                  color: roleFilter === "admin" ? brand.parchment : brand.slate,
                  "&:hover": { bgcolor: roleFilter === "admin" ? brand.ink800 : `${brand.slateLight}40` },
                }}
              />
            </Box>

            {/* View Switcher */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => v && setViewMode(v)}
              size="small"
              sx={{
                bgcolor: `${brand.slateLight}20`,
                p: 0.4,
                borderRadius: "12px",
                "& .MuiToggleButton-root": {
                  border: "none",
                  borderRadius: "9px",
                  px: 1.5,
                  py: 0.5,
                  color: brand.slate,
                  "&.Mui-selected": {
                    bgcolor: brand.paper,
                    color: brand.ink950,
                    boxShadow: "0 2px 8px rgba(11,10,61,0.08)",
                  },
                },
              }}
            >
              <ToggleButton value="table">
                <MdViewList size={18} />
              </ToggleButton>
              <ToggleButton value="grid">
                <MdGridView size={18} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Paper>

      {/* ── Table vs Grid View ─────────────────────────────────────────── */}
      {viewMode === "table" ? (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: "22px",
            overflow: "hidden",
            border: `1px solid ${brand.slateLight}40`,
            bgcolor: brand.paper,
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Staff Name</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Email Address</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Mobile Contact</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Staff Role</TableCell>
                <TableCell align="right" sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: rowsPerPage }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" height={28} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedEmployees.length > 0 ? (
                paginatedEmployees.map((emp, index) => (
                  <TableRow
                    key={emp.id || emp._id || index}
                    sx={{ "&:hover": { bgcolor: `${brand.brassSoft}60` } }}
                  >
                    <TableCell
                      sx={{
                        color: brand.slate,
                        fontFamily: '"IBM Plex Mono", monospace',
                        fontSize: "0.82rem",
                        fontWeight: 600,
                      }}
                    >
                      {String(page * rowsPerPage + index + 1).padStart(2, "0")}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar
                          sx={{
                            width: 38,
                            height: 38,
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            bgcolor: avatarBg(emp.role),
                            color: brand.parchment,
                          }}
                        >
                          {initials(emp.name) || <MdPersonOutline size={20} />}
                        </Avatar>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: brand.ink950 }}>
                          {emp.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: brand.slate, fontSize: "0.85rem", fontWeight: 500 }}>
                      {emp.email}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: brand.slate,
                        fontSize: "0.85rem",
                        fontFamily: '"IBM Plex Mono", monospace',
                      }}
                    >
                      {emp.mobile || "—"}
                    </TableCell>
                    <TableCell>
                      <Chip label={emp.role || "employee"} size="small" sx={roleChipStyle(emp.role)} />
                    </TableCell>
                    <TableCell align="right">
                      <MuiTooltip title="Edit Staff Member">
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(emp)}
                          sx={{
                            bgcolor: `${brand.slateLight}25`,
                            color: brand.ink950,
                            "&:hover": { bgcolor: brand.brassSoft },
                          }}
                        >
                          <MdEdit size={16} />
                        </IconButton>
                      </MuiTooltip>

                      <MuiTooltip title="Delete Staff Member">
                        <IconButton
                          size="small"
                          onClick={() => openDeleteDialog(emp)}
                          sx={{
                            bgcolor: "#fee2e2",
                            color: "#dc2626",
                            "&:hover": { bgcolor: "#fca5a5" },
                            ml: 1,
                          }}
                        >
                          <MdDelete size={16} />
                        </IconButton>
                      </MuiTooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <MdPersonOutline size={40} color={brand.slateLight} />
                    <Typography
                      sx={{
                        fontFamily: '"Fraunces", serif',
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: brand.ink950,
                        mt: 1,
                      }}
                    >
                      No employee records found
                    </Typography>
                    <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
                      {search || roleFilter !== "all"
                        ? "Try adjusting your search criteria."
                        : "Click 'Create Employee' to add your first staff member."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredEmployees.length}
            page={page}
            onPageChange={(e, n) => setPage(n)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </TableContainer>
      ) : (
        /* Grid View */
        <Grid container spacing={3} mb={3}>
          {paginatedEmployees.map((emp) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={emp.id || emp._id}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "22px",
                  bgcolor: brand.paper,
                  border: `1px solid ${brand.slateLight}40`,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "transform 200ms ease, box-shadow 200ms ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 18px 40px -15px rgba(11,10,61,0.12)",
                    borderColor: `${brand.brass}66`,
                  },
                }}
              >
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        fontSize: "1rem",
                        fontWeight: 700,
                        bgcolor: avatarBg(emp.role),
                        color: brand.parchment,
                        boxShadow: "0 6px 16px rgba(11,10,61,0.15)",
                      }}
                    >
                      {initials(emp.name) || <MdPersonOutline size={24} />}
                    </Avatar>
                    <Chip label={emp.role || "employee"} size="small" sx={roleChipStyle(emp.role)} />
                  </Box>

                  <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: brand.ink950 }} noWrap>
                    {emp.name}
                  </Typography>

                  <Box display="flex" alignItems="center" gap={1} mt={1} color={brand.slate}>
                    <MdEmail size={15} />
                    <Typography sx={{ fontSize: "0.8rem" }} noWrap>
                      {emp.email}
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1} mt={0.6} color={brand.slate}>
                    <MdPhone size={15} />
                    <Typography sx={{ fontSize: "0.8rem", fontFamily: '"IBM Plex Mono", monospace' }}>
                      {emp.mobile || "—"}
                    </Typography>
                  </Box>
                </Box>

                <Box display="flex" justifyContent="flex-end" gap={1} mt={3} pt={2} borderTop={`1px solid ${brand.slateLight}30`}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => openEditDialog(emp)}
                    startIcon={<MdEdit />}
                    sx={{
                      borderRadius: "10px",
                      borderColor: `${brand.slateLight}60`,
                      color: brand.ink950,
                      fontWeight: 600,
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => openDeleteDialog(emp)}
                    startIcon={<MdDelete />}
                    sx={{
                      borderRadius: "10px",
                      fontWeight: 600,
                    }}
                  >
                    Delete
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Create Employee Dialog ─────────────────────────────────────── */}
      <Dialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        PaperProps={{ sx: { borderRadius: "22px", p: 1, width: "100%", maxWidth: 480 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.35rem" }}>
          Create New Employee Profile
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleCreate} mt={1}>
            <TextField
              label="Staff Name"
              name="name"
              value={form.name}
              onChange={handleCreateChange}
              required
              fullWidth
              margin="normal"
            />
            <TextField
              label="Email Address"
              name="email"
              type="email"
              value={form.email}
              onChange={handleCreateChange}
              required
              fullWidth
              margin="normal"
            />
            <TextField
              label="Mobile Number"
              name="mobile"
              value={form.mobile}
              onChange={handleCreateChange}
              fullWidth
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Staff Role</InputLabel>
              <Select name="role" value={form.role} label="Staff Role" onChange={handleCreateChange}>
                <MenuItem value="employee">Employee / Field Staff</MenuItem>
                <MenuItem value="vendor">Vendor / Supplier</MenuItem>
                <MenuItem value="admin">Administrator</MenuItem>
              </Select>
            </FormControl>

            <DialogActions sx={{ px: 0, pt: 3 }}>
              <Button onClick={() => setOpenCreate(false)} sx={{ color: brand.slate, fontWeight: 600 }}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                disabled={creating}
                sx={{ fontWeight: 700, px: 3, borderRadius: "10px" }}
              >
                {creating ? "Creating..." : "Create Employee"}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── Edit Employee Dialog ───────────────────────────────────────── */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        PaperProps={{ sx: { borderRadius: "22px", p: 1, width: "100%", maxWidth: 460 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.35rem" }}>
          Edit Employee Credentials
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Staff Name"
            name="name"
            value={editForm.name}
            onChange={handleEditChange}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Email Address"
            name="email"
            value={editForm.email}
            onChange={handleEditChange}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Mobile Number"
            name="mobile"
            value={editForm.mobile}
            onChange={handleEditChange}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Staff Role</InputLabel>
            <Select name="role" value={editForm.role} label="Staff Role" onChange={handleEditChange}>
              <MenuItem value="employee">Employee / Field Staff</MenuItem>
              <MenuItem value="vendor">Vendor / Supplier</MenuItem>
              <MenuItem value="admin">Administrator</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpenEdit(false)} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleUpdate}
            disabled={updating}
            sx={{ fontWeight: 700, px: 3, borderRadius: "10px" }}
          >
            {updating ? "Saving..." : "Update Employee"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation Dialog ──────────────────────────────────── */}
      <Dialog
        open={openDelete}
        onClose={() => !deleting && setOpenDelete(false)}
        PaperProps={{ sx: { borderRadius: "20px", p: 1, maxWidth: 420 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontFamily: '"Fraunces", serif', color: brand.ink950 }}>
          Delete Employee
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: brand.slate, fontSize: "0.95rem" }}>
            Are you sure you want to delete staff member <strong>{deleteEmp?.name}</strong> ({deleteEmp?.email})? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpenDelete(false)} disabled={deleting} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ fontWeight: 700, px: 3, borderRadius: "10px" }}
          >
            {deleting ? "Deleting..." : "Delete Employee"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Employee;
